import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { nodeToDOM } from '@common/util/schema';
import { generateSvgDataQrCode } from '@juit/qrcode';
import { memoize } from 'lodash';
import { NodeSpec } from 'prosemirror-model';

// Memoize the QR code generation to improve performance
const memoizedGenerateSvgDataQrCode = memoize(generateSvgDataQrCode);

export class MadCapQRCodeSchemaPlugin extends SchemaPlugin {
  nodes = {
    madcapqrcode: {
      group: 'inline',
      inline: true,
      content: 'text*',
      atom: true,
      definingForContent: true,
      parseDOM: [{
        tag: 'MadCap\\:qrCode'
      }],
      // Render frame as mc-lwe-qrcode to use the custom element in the editor
      toDOM(node) {
        const outputSpec = nodeToDOM(node, { isVoid: true, tagName: 'mc-lwe-qrcode' });
        const content = node.textContent;
        (outputSpec.dom as HTMLImageElement).src = content ? memoizedGenerateSvgDataQrCode(content, { margin: 0 }) : null;
        return outputSpec;
      },
      tagName: 'MadCap:qrCode'
    }
  } satisfies Record<string, NodeSpec>;
}

// MadCap:qrCode is rendered as the mc-lwe-qrcode custom element. Define it here.
if (typeof window !== 'undefined') {
  class McLweQrCodeElement extends HTMLElement {
    static get observedAttributes() { return ['src', 'alt', 'title']; }
    private img: HTMLImageElement;

    constructor() {
      super();
      const root = this.attachShadow({ mode: 'open' });

      // Ensure host has a box and img fills it
      // Render the img with a white background to match the Flare compiler
      const style = document.createElement('style');
      style.textContent = `
        :host {
          display: inline-block;
          box-sizing: border-box;
          width: 120px;
          height: 120px;
        }
        img {
          display: inline-block;
          width: 100%;
          height: 100%;
          object-fit: fill;
          background: white;
        }
      `;
      root.appendChild(style);

      this.img = document.createElement('img');
      root.appendChild(this.img);
    }

    // Proxy all observed attributes to the img element
    attributeChangedCallback(name: string, _old: string | null, value: string | null) {
      if (value == null) {
        this.img.removeAttribute(name);
      } else {
        this.img.setAttribute(name, value);
      }
    }

    // Set the src attribute when the property is set
    get src() { return this.getAttribute('src') ?? ''; }
    set src(value: string) { this.setAttribute('src', value); }
  }

  customElements.define('mc-lwe-qrcode', McLweQrCodeElement);
}
